/**
 * Depot context routes — /depot/:tenantId?/:externalDepotId?
 *
 * Mounted under the `depots` context guard in routes.ts. The parent route
 * renders DepotContextContainer plus a `LeftSidebar` named view for the
 * context navigation. Both params are optional because the empty-path child
 * (`depot`) shows the depot selector card until a depot is chosen; all other
 * children assume a selected tenant/depot.
 *
 * Some children render through keyed named views (e.g. `subcontractorDrivers`,
 * `backofficeUsers`): the parent container owns the layout and decides which
 * named router-view to display.
 */

import { type RouteRecordRaw } from 'vue-router';

const UsageAnalysisContainer = () =>
  import('@/views/depot/UsageAnalysisContainer.vue');
const DevicesContainer = () => import('@/views/depot/DevicesContainer.vue');
const UploadJobsContainer = () =>
  import('@/views/depot/UploadJobsContainer.vue');
const UploadJobsDetailsContainer = () =>
  import('@/views/depot/UploadJobsDetailsContainer.vue');
const DepotContextContainer = () =>
  import('@/contexts/depot/DepotContextContainer.vue');
const DriversContainer = () =>
  import('@/contexts/depot/drivers/DriversContainer.vue');
const LiveStatusTours = () =>
  import('@/contexts/depot/liveStatus/LiveStatusTours.vue');
const LiveStatusTourDetails = () =>
  import('@/contexts/depot/liveStatus/LiveStatusTourDetails.vue');
const DepotDashboard = () =>
  import('@/contexts/depot/depotDashboard/DepotDashboard.vue');
const LoadingAndDispatchDashboard = () =>
  import('@/contexts/depot/depotDashboard/LoadingAndDispatchDashboard.vue');
const TourPerformance = () => import('@/views/depot/TourPerformance.vue');
const CollectionsCockpit = () => import('@/views/depot/CollectionsCockpit.vue');
const TourPerformanceDetails = () =>
  import('@/views/depot/TourPerformanceDetails.vue');
const TourDrivenHistoryV2 = () =>
  import('@/contexts/depot/tourPerformance/drivenHistoryV2/TourDrivenHistory.vue');

const MainErrorComponent = () =>
  import('@/components/errorHandling/MainErrorComponent.vue');
const AddressLocationValidation = () =>
  import('@/contexts/depot/addressLocationValidation/AddressLocationValidationContainer.vue');
const SubcontractorList = () =>
  import('@/contexts/depot/subcontractor/SubcontractorList.vue');
const UsersContainer = () => import('@/components/users/UsersContainer.vue');
const UsersList = () => import('@/components/users/UsersList.vue');

const DepotLandingContainer = () =>
  import('@/views/depot/DepotLandingContainer.vue');

const DepotContextNavigation = () =>
  import('@/contexts/depot/DepotContextNavigation.vue');

const depotChildRoutes: RouteRecordRaw[] = [
  {
    component: DepotLandingContainer,
    name: 'depotLanding',
    path: 'depot-landing',
    meta: {
      titleKey: 'mainNavigation.depotLanding'
    }
  },
  {
    component: UsageAnalysisContainer,
    name: 'dailyActivities',
    path: 'statistics/activities/:date?',
    props: true,
    meta: {
      titleKey: 'mainNavigation.usageAnalysation'
    }
  },
  {
    path: 'statistics/live-status',
    name: 'liveStatus',
    redirect: { name: 'liveStatusTours' },
    children: [
      {
        component: LiveStatusTours,
        name: 'liveStatusTours',
        path: 'tours',
        meta: {
          titleKey: 'mainNavigation.liveStatus'
        }
      },
      {
        component: LiveStatusTourDetails,
        name: 'tourView',
        path: 'tours/:tourId',
        meta: {
          titleKey: 'mainNavigation.liveStatusTours',
          remountOnParamsChange: true
        }
      }
    ]
  },
  {
    component: TourPerformance,
    name: 'tourPerformance',
    path: 'tour-performance',
    meta: {
      titleKey: 'mainNavigation.tourPerformance'
    }
  },
  {
    component: CollectionsCockpit,
    name: 'collectionsCockpit',
    path: 'collections',
    meta: {
      fillHeight: true,
      titleKey: 'mainNavigation.collectionsCockpit',
      remountOnParamsChange: true
    }
  },
  {
    component: TourPerformanceDetails,
    name: 'tourPerformanceDetails',
    path: 'tour-performance/details/:tour/:dates',
    props: true,
    meta: {
      titleKey: 'mainNavigation.tourPerformance',
      remountOnParamsChange: true
    }
  },
  {
    component: TourDrivenHistoryV2,
    name: 'tourDrivenHistoryV2',
    path: 'tour-performance/history/v2/:tourId',
    props: true,
    meta: {
      titleKey: 'mainNavigation.tourPerformance'
    }
  },
  {
    component: DepotDashboard,
    name: 'depotDashboard',
    path: 'depot-dashboard',
    props: true,
    meta: {
      titleKey: 'mainNavigation.depotDashboard'
    }
  },
  {
    component: LoadingAndDispatchDashboard,
    name: 'loadingAndDispatchDashboard',
    path: 'loading-and-dispatch-dashboard',
    props: true,
    meta: {
      titleKey: 'mainNavigation.loadingAndDispatchDashboard'
    }
  },
  {
    component: DevicesContainer,
    name: 'deviceManagement',
    path: 'deviceManagement',
    props: true,
    meta: {
      titleKey: 'mainNavigation.devices'
    }
  },
  {
    component: UploadJobsContainer,
    name: 'uploadJobs',
    path: 'uploadJobs/:date?',
    props: true,
    meta: {
      titleKey: 'uploadJobs.uploadJobs'
    }
  },
  {
    component: UploadJobsDetailsContainer,
    name: 'uploadJobsDetails',
    path: 'uploadJobs/:date/details/:vehicle',
    props: true,
    meta: {
      titleKey: 'uploadJobs.uploadJobs'
    }
  },
  {
    component: AddressLocationValidation,
    name: 'addressLocationValidation',
    path: 'addressLocationValidation',
    props: true,
    meta: {
      titleKey: 'addressCorrections.addressCorrection'
    }
  },
  {
    component: SubcontractorList,
    path: 'subcontractor',
    name: 'subcontractorList',
    meta: { fillHeight: true, titleKey: 'mainNavigation.subContractor' }
  },
  {
    children: [
      {
        components: {
          subcontractorDrivers: DriversContainer
        },
        name: 'subcontractorDrivers',
        path: 'drivers'
      }
    ],
    component: UsersContainer,
    name: 'subcontractorUser',
    path: 'subcontractor/:sucoId',
    props: true,
    meta: {
      titleKey: 'mainNavigation.subContractor'
    }
  },
  {
    name: 'tempDriver',
    path: 'tempDriver',
    redirect: 'users/tempDrivers'
  },
  {
    children: [
      {
        components: {
          backofficeUsers: UsersList
        },
        name: 'backofficeUsers',
        path: 'backoffice',
        meta: {
          titleKey: 'userManagement.names.backofficeUser'
        }
      },
      {
        components: {
          tempDrivers: DriversContainer
        },
        name: 'tempDrivers',
        path: 'tempDrivers',
        meta: {
          titleKey: 'mainNavigation.temporaryUserAccounts'
        }
      },
      {
        components: {
          userError: MainErrorComponent
        },
        name: 'userError',
        path: 'error/:errorCode',
        props: {
          userError: true
        }
      }
    ],
    component: UsersContainer,
    name: 'users',
    path: 'users'
  }
];

export const depotRoute: RouteRecordRaw = {
  path: '/depot/:tenantId?/:externalDepotId?',
  components: {
    default: DepotContextContainer,
    LeftSidebar: DepotContextNavigation
  },
  props: true,
  children: [
    {
      path: '',
      name: 'depot',
      component: () => import('@/contexts/depot/DepotSelectorCard.vue'),
      meta: {
        fillHeight: true,
        hasBasicLayout: true
      }
    },
    ...depotChildRoutes
  ]
};
